import { useEffect, useState } from 'react';
import { PulseLoader } from 'react-spinners';
import ActionStatusText from '../../components/ui/ActionStatusText';
import ErrorMessage from '../../components/ui/ErrorMessage';
import { useModalNavigation, useModalPayload } from '../../context/ModalControllerContext';
import { useModalEntry } from '../../context/ModalEntryContext';
import useApplyFavoriteSession from '../../hooks/data/exercise-sessions/favorite/useApplyFavoriteSession';
import useDeleteFavoriteExerciseSession from '../../hooks/data/exercise-sessions/favorite/useDeleteFavoriteSession';
import useDeleteFavoriteSessionItem from '../../hooks/data/exercise-sessions/favorite/useDeleteFavoriteSessionItem';
import useDeleteExercise from '../../hooks/data/exercises/useDeleteExercise';
import useIsOverflowing from '../../hooks/ui/useIsOverflowing';
import ModalHeader from '../components/ModalHeader';
import ModalExerciseEditor from './ModalExerciseEditor';
import ModalFavoriteItemEditor from './ModalFavoriteItemEditor';
import { useDiarySection } from '../../context/DiarySectionsContext';
import { formatDistance, formatDuration, formatExercisePrefix, formatSteps } from '../../helpers/domain';

const ModalExerciseManager = ({ currentDay }) => {
    const { selectedEntry } = useModalEntry();
    const payload = useModalPayload();

    const { close, closeWithDelay } = useModalNavigation();
    const { closeSection } = useDiarySection();

    // DERIVED MODE
    const isFavoriteMode = payload?.actionType === 'favorite';
    const favoriteSession = payload?.entry ?? null;

    // DATA
    const exercises = isFavoriteMode ? favoriteSession?.items ?? [] : selectedEntry?.items ?? [];

    const { deleteExercise, isDeleting, isError, error } = useDeleteExercise(currentDay);
    const { applyFavorite, isApplying, isSuccess } = useApplyFavoriteSession(currentDay);
    const { mutateAsync: deleteFavoriteItem } = useDeleteFavoriteSessionItem();
    const {
        mutateAsync: deleteFavoriteSession,
        isPending: isDeletingSession,
        isSuccess: isDeletingSessionSuccess
    } = useDeleteFavoriteExerciseSession();

    // LOCAL STATE
    const [editableExercises, setEditableExercises] = useState(exercises);
    const [view, setView] = useState('list');
    const [selectedExercise, setSelectedExercise] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    // UI
    const {
        ref: overflowRef,
        isOverflowing,
        checkOverflow
    } = useIsOverflowing([editableExercises.length, view]);

    const hasExercises = editableExercises.length > 0;
    const isProcessing =
        isApplying ||
        isDeleting ||
        isDeletingSession ||
        !hasExercises ||
        isSuccess ||
        isDeletingSessionSuccess;

    // ACTIONS

    const handleApplyFavorite = async () => {
        if (!favoriteSession) return;

        try {
            await applyFavorite({
                favoriteId: favoriteSession.id,
                date: currentDay
            });

            closeWithDelay();
        } catch (err) {
            console.error('Failed to apply favorite session', err);
        }
    };

    const handleDelete = async (id) => {
        try {
            setDeletingId(id);

            if (isFavoriteMode) {
                const isLastItem = editableExercises.length === 1;

                await deleteFavoriteItem(id);

                if (isLastItem && favoriteSession) {
                    await deleteFavoriteSession(favoriteSession.id);

                    closeSection('exercise');
                    closeWithDelay();
                    return;
                }
            } else {
                await deleteExercise(id);

                const isLastItem = editableExercises.length === 1;

                if (isLastItem) {
                    closeSection('exercise');
                    closeWithDelay();
                    return;
                }
            }

            setEditableExercises((prev) => prev.filter((item) => item.id !== id));
        } finally {
            setDeletingId(null);
        }
    };

    const handleSelect = (exercise) => {
        setSelectedExercise(exercise);
        setView('edit');
    };

    const handleBack = () => {
        setSelectedExercise(null);
        setView('list');
    };

    // EFFECTS

    useEffect(() => checkOverflow(), [editableExercises, view]);
    useEffect(() => setEditableExercises(exercises), [exercises]);

    return (
        <>
            {view === 'list' && (
                <>
                    <ModalHeader
                        title="Dagens motion"
                        onClose={close}
                        showBack={false}
                        disableButtons={!hasExercises || isDeleting}
                    />

                    <div
                        ref={overflowRef}
                        className={`modal-body exercise-list ${isOverflowing ? 'has-scroll' : ''}`}
                    >
                        {isError && (
                            <ErrorMessage message={error?.message || 'Kunde inte radera motionen.'} />
                        )}

                        {hasExercises ? (
                            <ul>
                                {editableExercises.map((item) => {
                                    const type = Number(item.type);
                                    const isSteps = type === 2;

                                    const formattedDistance = formatDistance(item.distance_km);
                                    const formattedSteps = formatSteps(item.steps);
                                    const duration = formatDuration(item.duration);

                                    return (
                                        <li key={item.id} className="exercise-item">
                                            <div className="top">
                                                <div className="item-left exercise-name">
                                                    <span>
                                                        {formatExercisePrefix({
                                                            isSteps,
                                                            time: item.time,
                                                            duration,
                                                            distance: formattedDistance
                                                        })}{' '}
                                                    </span>
                                                    {!isSteps ? item.name : formattedSteps}
                                                </div>
                                                <span className="item-kcal">
                                                    {Math.round(item.kcal)} kcal
                                                </span>
                                            </div>

                                            <div className="bottom">
                                                <div className="comment">
                                                    {item.comment ? (
                                                        <p>
                                                            Kommentar: <span>{item.comment}</span>
                                                        </p>
                                                    ) : (
                                                        <p className="no-comment">Ingen kommentar</p>
                                                    )}
                                                </div>

                                                <div className="exercise-actions">
                                                    <button
                                                        type="button"
                                                        className="edit-item-btn"
                                                        onClick={() => handleSelect(item)}
                                                        disabled={isProcessing}
                                                    >
                                                        <img
                                                            src="/assets/images/diary-icons/pen-white.svg"
                                                            alt="Redigera"
                                                        />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="delete-item-btn"
                                                        onClick={() => handleDelete(item.id)}
                                                        disabled={isProcessing}
                                                    >
                                                        {deletingId !== item.id ? (
                                                            <img
                                                                src="/assets/images/diary-icons/times-white.svg"
                                                                alt="Radera"
                                                            />
                                                        ) : (
                                                            <PulseLoader color="#fff" size={4} />
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <div className="no-exercise-left">
                                <p>Inga motioner kvar för idag.</p>
                            </div>
                        )}
                    </div>

                    {isFavoriteMode && (
                        <div className="modal-footer exercise">
                            <button
                                type="button"
                                className="btn-green bold-title"
                                onClick={handleApplyFavorite}
                                disabled={isProcessing}
                            >
                                <ActionStatusText
                                    isProcessing={isApplying || isDeletingSession}
                                    isCompleted={isSuccess || isDeletingSessionSuccess}
                                    defaultLabel="Spara favoritpass"
                                    processingLabel={isDeletingSession ? 'Raderar' : 'Sparar'}
                                    successLabel={
                                        isDeletingSessionSuccess
                                            ? 'Favoritpass raderat'
                                            : 'Favoritpass sparat!'
                                    }
                                />
                            </button>
                        </div>
                    )}
                </>
            )}

            {view === 'edit' &&
                selectedExercise &&
                (!isFavoriteMode ? (
                    <ModalExerciseEditor
                        exercise={selectedExercise}
                        currentDay={currentDay}
                        onClose={close}
                        onGoBack={handleBack}
                    />
                ) : (
                    <ModalFavoriteItemEditor
                        exercise={selectedExercise}
                        onClose={close}
                        onGoBack={handleBack}
                        onSave={(updated) =>
                            setEditableExercises((prev) =>
                                prev.map((item) => (item.id === updated.id ? updated : item))
                            )
                        }
                    />
                ))}
        </>
    );
};

export default ModalExerciseManager;
